import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  loading: false,
  error: null,
  user: null,
  permissions: null,
  isAuthenticated: false,
  // 2FA state
  requires2FA: false,
  twoFactorUsername: null, // Store username for 2FA login
  twoFactorPassword: null, // Store password for 2FA login
  twoFactorMethods: [], // Available 2FA methods from BE: [{ method: 1, key: 'sms', label: 'SMS', available: true, maskedContact: '****1234' }]
  twoFactorSelectedMethod: null, // Selected method object or method number
  twoFactorMaskedContact: null, // Masked phone/email for display
  twoFactorCodeSent: false,
  twoFactorResendCountdown: 0,
  twoFactorAutoSendTriggered: false, // Track if auto-send was triggered
  // Password expiry state
  requiresPasswordChange: false,
  passwordExpiredMessage: null,
  passwordChangeUsername: null, // Store username for password change
  passwordChangeLoading: false,
  passwordChangeError: null,
};

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    // Action to trigger login saga
    loginRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    // Action dispatched by saga on success
    loginSuccess: (state, action) => {
      state.loading = false;
      state.user = action.payload.user || action.payload;
      state.permissions = action.payload.permissions || null;
      state.isAuthenticated = true;
      state.error = null;
      // Clear 2FA state on successful login
      state.requires2FA = false;
      state.twoFactorUsername = null;
      state.twoFactorPassword = null;
      state.twoFactorMethods = [];
      state.twoFactorSelectedMethod = null;
      state.twoFactorMaskedContact = null;
      state.twoFactorCodeSent = false;
      state.twoFactorAutoSendTriggered = false;
    },
    // Action dispatched by saga on failure
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
    },
    // Action to logout
    logout: (state) => {
      state.user = null;
      state.permissions = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear 2FA state on logout
      state.requires2FA = false;
      state.twoFactorUsername = null;
      state.twoFactorPassword = null;
      state.twoFactorMethods = [];
      state.twoFactorSelectedMethod = null;
      state.twoFactorMaskedContact = null;
      state.twoFactorCodeSent = false;
      state.twoFactorResendCountdown = 0;
      state.twoFactorAutoSendTriggered = false;
      // Clear password expiry state on logout
      state.requiresPasswordChange = false;
      state.passwordExpiredMessage = null;
      state.passwordChangeUsername = null;
    },
    // Action to clear error
    clearError: (state) => {
      state.error = null;
    },
    // Action to trigger fetchMe saga
    fetchMeRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    // Action dispatched by saga on fetchMe success
    fetchMeSuccess: (state, action) => {
      state.loading = false;
      state.user = action.payload.user || action.payload;
      state.permissions = action.payload.permissions || null;
      state.isAuthenticated = true;
      state.error = null;
    },
    // Action dispatched by saga on fetchMe failure
    fetchMeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      // Don't change authentication state on failure - user might still be authenticated
    },

    // ==================== 2FA Actions ====================

    // Action dispatched when login requires 2FA
    twoFactorRequired: (state, action) => {
      state.loading = false;
      state.requires2FA = true;
      state.twoFactorUsername = action.payload.username;
      state.twoFactorPassword = action.payload.password;
      state.twoFactorMethods = action.payload.methods || []; // Array of { method, key, label, available, maskedContact }
      state.twoFactorMaskedContact = action.payload.maskedContact || null;
      state.twoFactorAutoSendTriggered = false;
      state.error = null;
    },

    // Action to mark auto-send as triggered
    setAutoSendTriggered: (state) => {
      state.twoFactorAutoSendTriggered = true;
    },

    // Action to select 2FA method
    selectTwoFactorMethod: (state, action) => {
      state.twoFactorSelectedMethod = action.payload;
      state.twoFactorCodeSent = false;
      state.error = null;
    },

    // Action to trigger send 2FA code saga
    sendTwoFactorCodeRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },

    // Action dispatched when 2FA code is sent successfully
    sendTwoFactorCodeSuccess: (state, action) => {
      state.loading = false;
      state.twoFactorCodeSent = true;
      state.twoFactorMaskedContact = action.payload.maskedContact || state.twoFactorMaskedContact;
      state.twoFactorResendCountdown = action.payload.resendCountdown ?? 0;
      state.error = null;
    },

    // Action dispatched when send 2FA code fails
    sendTwoFactorCodeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Action to trigger verify 2FA code saga
    verifyTwoFactorCodeRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },

    // Action dispatched when 2FA verification fails
    verifyTwoFactorCodeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Action to trigger resend 2FA code saga
    resendTwoFactorCodeRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },

    // Action dispatched when resend is successful
    resendTwoFactorCodeSuccess: (state, action) => {
      state.loading = false;
      state.twoFactorResendCountdown = action.payload.resendCountdown ?? 0;
      state.error = null;
    },

    // Action dispatched when resend fails
    resendTwoFactorCodeFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Action to update resend countdown
    updateResendCountdown: (state, action) => {
      state.twoFactorResendCountdown = action.payload;
    },

    // Action to cancel 2FA and go back to login
    cancelTwoFactor: (state) => {
      state.requires2FA = false;
      state.twoFactorUsername = null;
      state.twoFactorPassword = null;
      state.twoFactorMethods = [];
      state.twoFactorSelectedMethod = null;
      state.twoFactorMaskedContact = null;
      state.twoFactorCodeSent = false;
      state.twoFactorResendCountdown = 0;
      state.twoFactorAutoSendTriggered = false;
      state.error = null;
    },

    // ==================== Password Change Actions ====================

    // Action dispatched when password change is required
    passwordChangeRequired: (state, action) => {
      state.loading = false;
      state.requiresPasswordChange = true;
      state.passwordExpiredMessage = action.payload.message;
      state.passwordChangeUsername = action.payload.username;
      state.error = null;
    },

    // Action to cancel password change
    cancelPasswordChange: (state) => {
      state.requiresPasswordChange = false;
      state.passwordExpiredMessage = null;
      state.passwordChangeUsername = null;
      state.passwordChangeLoading = false;
      state.passwordChangeError = null;
    },

    // Action to trigger change password expired saga
    changePasswordExpiredRequest: (state) => {
      state.passwordChangeLoading = true;
      state.passwordChangeError = null;
    },

    // Action dispatched when password change is successful
    changePasswordExpiredSuccess: (state) => {
      state.passwordChangeLoading = false;
      state.passwordChangeError = null;
      // Don't clear requiresPasswordChange here - it will be cleared after successful login
    },

    // Action dispatched when password change fails
    changePasswordExpiredFailure: (state, action) => {
      state.passwordChangeLoading = false;
      state.passwordChangeError = action.payload;
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  fetchMeRequest,
  fetchMeSuccess,
  fetchMeFailure,
  // 2FA actions
  twoFactorRequired,
  setAutoSendTriggered,
  selectTwoFactorMethod,
  sendTwoFactorCodeRequest,
  sendTwoFactorCodeSuccess,
  sendTwoFactorCodeFailure,
  verifyTwoFactorCodeRequest,
  verifyTwoFactorCodeFailure,
  resendTwoFactorCodeRequest,
  resendTwoFactorCodeSuccess,
  resendTwoFactorCodeFailure,
  updateResendCountdown,
  cancelTwoFactor,
  // Password change actions
  passwordChangeRequired,
  cancelPasswordChange,
  changePasswordExpiredRequest,
  changePasswordExpiredSuccess,
  changePasswordExpiredFailure,
} = loginSlice.actions;

// Selectors
export const selectLoginLoading = (state) => state.login?.loading;
export const selectLoginError = (state) => state.login?.error;
export const selectLoginUser = (state) => state.login?.user;
export const selectPermissions = (state) => state.login?.permissions;
export const selectIsAuthenticated = (state) => state.login?.isAuthenticated;

// 2FA Selectors
export const selectRequires2FA = (state) => state.login?.requires2FA;
export const selectTwoFactorUsername = (state) => state.login?.twoFactorUsername;
export const selectTwoFactorPassword = (state) => state.login?.twoFactorPassword;
export const selectTwoFactorMethods = (state) => state.login?.twoFactorMethods;
export const selectTwoFactorSelectedMethod = (state) => state.login?.twoFactorSelectedMethod;
export const selectTwoFactorMaskedContact = (state) => state.login?.twoFactorMaskedContact;
export const selectTwoFactorCodeSent = (state) => state.login?.twoFactorCodeSent;
export const selectTwoFactorResendCountdown = (state) => state.login?.twoFactorResendCountdown;
export const selectTwoFactorAutoSendTriggered = (state) => state.login?.twoFactorAutoSendTriggered;

// Password change selectors
export const selectRequiresPasswordChange = (state) => state.login?.requiresPasswordChange;
export const selectPasswordExpiredMessage = (state) => state.login?.passwordExpiredMessage;
export const selectPasswordChangeUsername = (state) => state.login?.passwordChangeUsername;
export const selectPasswordChangeLoading = (state) => state.login?.passwordChangeLoading;
export const selectPasswordChangeError = (state) => state.login?.passwordChangeError;

export default loginSlice.reducer;
